import React, { useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { 
  Sparkles, 
  Bug, 
  Zap, 
  Palette,
  Keyboard,
  Printer,
  Layout,
  Users,
  Shield,
  Database,
  FileText,
  Globe,
  Activity,
  Settings,
  PanelLeftClose,
  History,
  BookOpen,
  ChevronDown,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';

interface ReleaseNotesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type PermissionType = Parameters<ReturnType<typeof useAuth>['hasPermission']>[0];

interface ChangeEntry {
  type: 'feature' | 'improvement' | 'fix';
  icon?: React.ReactNode;
  title: string;
  description: string;
  /** Optional in-depth, step-by-step guide shown in an expandable block */
  guide?: {
    intro?: string;
    steps?: { title: string; detail: string }[];
    table?: { title: string; rows: { label: string; value: string }[] };
    tips?: string[];
  };
  /** If set, only users with at least one of these permissions see this entry */
  requiredPermissions?: PermissionType[];
  /** If true, only admins see this entry */
  adminOnly?: boolean;
}

interface ReleaseNote {
  version: string;
  date: string;
  highlights?: string;
  changes: ChangeEntry[];
}

const releaseNotes: ReleaseNote[] = [
  {
    version: '1.7',
    date: 'August 12, 2026',
    highlights: 'Scanner-Tasten (Enter, Tab, Strg+S …) für Barcodes, Data Matrix und QR-Codes',
    changes: [
      {
        type: 'feature',
        icon: <Keyboard className="w-4 h-4" />,
        title: 'Scanner-Tasten für Barcodes & QR-Codes',
        description:
          'Jeder Barcode-, Data-Matrix- und QR-Code-Element kann jetzt eine Tastenaktion vor und/oder nach den Daten mitschicken – z. B. Enter, Tab, Escape oder Strg+S. Der Scanner (Keyboard-Wedge-Modus) tippt die Taste automatisch, sobald der Code gelesen wurde.',
        guide: {
          intro:
            'Die Tasten werden als ASCII-Steuerzeichen direkt in die Code-Daten eingebettet. Ein Scanner im Tastatur-Modus gibt diese Steuerzeichen als echten Tastendruck an den PC weiter – ohne dass am Scanner selbst etwas konfiguriert werden muss.',
          steps: [
            {
              title: '1. Element auswählen',
              detail:
                'Im Editor auf den Barcode, die Data Matrix oder den QR-Code klicken. Die Eigenschaften erscheinen in der rechten Seitenleiste.',
            },
            {
              title: '2. Abschnitt „Scanner Keys" öffnen',
              detail:
                'Unterhalb von „Barcode Settings" den Bereich „Scanner Keys" aufklappen (Tastatur-Symbol). Er ist standardmäßig zugeklappt und wird pro Nutzer gespeichert.',
            },
            {
              title: '3. Suffix-Taste wählen',
              detail:
                '„Suffix key" ist der Normalfall: Die Taste wird NACH dem Wert gesendet. Beispiel: „Enter (CR)" bestätigt das Feld in Excel/ERP, „Tab" springt zum nächsten Eingabefeld.',
            },
            {
              title: '4. Optional: Prefix-Taste wählen',
              detail:
                '„Prefix key" wird VOR dem Wert gesendet – nützlich, um z. B. mit Strg+A ein Feld zu leeren oder mit Escape einen Dialog zurückzusetzen, bevor der Wert getippt wird.',
            },
            {
              title: '5. Tasten ZWISCHEN mehreren Werten',
              detail:
                'Für „wert1 Enter wert2 Enter wert3": Unter „Separator key (between values)" die Taste (z. B. Enter) wählen und im Inhalt die Werte mit dem Trennzeichen schreiben – standardmäßig „|", also wert1|wert2|wert3. Jedes „|" wird beim Codieren durch die gewählte Taste ersetzt. Das Trennzeichen selbst ist frei wählbar (Feld „Separator token"), falls „|" in den Daten vorkommt.',
            },
            {
              title: '6. Alternativ: Tasten direkt im Text',
              detail:
                'Man kann Tasten auch punktgenau im Inhalt platzieren: wert1{ENTER}wert2{TAB}wert3{CTRL+S}. Unterstützte Tokens: {ENTER}, {CR}, {TAB}, {LF}, {CRLF}, {ESC}, {SPACE} und {CTRL+A} … {CTRL+Z}. Funktioniert auch mit dynamischen Feldern, z. B. {{artikel}}{ENTER}{{menge}}{ENTER}.',
            },
            {
              title: '7. Testen und drucken',
              detail:
                'Der Code auf Canvas, Druckvorschau und im PDF wird sofort neu erzeugt. Zum Prüfen den Code scannen, während der Cursor in einem Texteditor steht – Wert und Tastendruck müssen wie erwartet ankommen.',
            },
          ],
          table: {
            title: 'Verfügbare Tasten und ihr Steuerzeichen',
            rows: [
              { label: 'Enter (CR)', value: 'CR / 0x0D – bestätigt Eingaben, häufigster Suffix' },
              { label: 'Tab', value: '0x09 – springt zum nächsten Eingabefeld' },
              { label: 'Line Feed (LF)', value: '0x0A – für Systeme, die LF statt CR erwarten' },
              { label: 'Enter + LF (CRLF)', value: '0x0D 0x0A – Windows-typische Zeilenendung' },
              { label: 'Escape', value: '0x1B – bricht Dialoge ab / setzt Felder zurück' },
              { label: 'Space', value: '0x20 – einfaches Trennzeichen' },
              { label: 'Strg+A … Strg+Z', value: '0x01 … 0x1A – z. B. Strg+S (Speichern), Strg+L, Strg+P (Drucken)' },
            ],
          },
          tips: [
            'Der lesbare Text unter dem Barcode bleibt sauber: Steuerzeichen werden nicht mit angezeigt, nur codiert.',
            'Mehrere Werte in einem Code: Ein Scan füllt so ganze Formulare – z. B. Artikelnummer, Tab, Menge, Enter.',
            'Leerzeichen um das Trennzeichen werden entfernt, „wert1 | wert2" ergibt also „wert1⏎wert2".',
            'Geeignete Symbologien: Code 128, Code 93, Telepen, Data Matrix, PDF417 und QR-Code (voller ASCII-Bereich).',
            'Nicht möglich bei rein numerischen Formaten wie EAN-13, EAN-8, UPC-A, ITF-14 oder Pharmacode – diese können keine Steuerzeichen tragen.',
            'Hinweis zur Doppelung: Strg+I entspricht Tab, Strg+J entspricht LF und Strg+M entspricht Enter – nur eine der Varianten setzen.',
            'Manche Scanner unterdrücken Steuerzeichen im Datenteil. Falls nichts ankommt, im Scanner-Setup „Function Code Transmit" bzw. „Control Character Output" aktivieren.',
            'Kombination möglich: Prefix „Strg+A" + Suffix „Enter" überschreibt ein bestehendes Feld komplett und bestätigt anschließend.',
          ],
        },
        requiredPermissions: ['manage_elements' as PermissionType],
      },
      {
        type: 'feature',
        icon: <FileText className="w-4 h-4" />,
        title: 'Text-Import für Batch-Druck',
        description:
          'Der Druckdialog hat einen neuen „Text Import"-Tab: Eine .txt-Datei mit key:value-Paaren wird in Batch-Daten umgewandelt und direkt auf die Vorlagen-Felder gemappt – ideal für ERP-Exporte, Serienbriefe oder manuelle Eingaben.',
        guide: {
          intro:
            'Der Parser erkennt die Schreibweisen key: value und key = value, unterstützt mehrzeilige Werte mit einem Backslash am Zeilenende und kann mehrere Datensätze durch Leerzeilen oder ein eigenes Trennzeichen trennen. Schlüssel werden grob an die Vorlagen-Felder angeglichen (Groß-/Kleinschreibung, Leerzeichen und Unterstriche werden ignoriert).',
          steps: [
            {
              title: '1. .txt-Datei vorbereiten',
              detail:
                'Jede Zeile enthält ein Feld im Format Feldname: Wert oder Feldname = Wert. Beispiel: productName: Schraube M6, quantity: 10. Mehrere Etiketten entweder durch eine Leerzeile trennen oder einen eigenen Separator (z. B. ---) verwenden.',
            },
            {
              title: '2. Druckdialog öffnen',
              detail:
                'Im Editor rechts unten den Bereich „Batch Data" aufklappen. Er enthält die Tabs „CSV Import" und „Text Import".',
            },
            {
              title: '3. Tab „Text Import" wählen',
              detail:
                'Auf „Text Import" wechseln. Dort erscheinen die Optionen für Modus, Trennzeichen und die Datei-Auswahl.',
            },
            {
              title: '4. Datei hochladen',
              detail:
                'Über die Datei-Auswahl eine .txt-Datei auswählen oder per Drag & Drop ablegen. Der Inhalt wird sofort eingelesen.',
            },
            {
              title: '5. Modus einstellen',
              detail:
                '„Single label" = die gesamte Datei bildet einen Datensatz (ein Etikett). „Multiple labels" = die Datei wird anhand von Leerzeilen oder dem eingestellten Separator in mehrere Datensätze aufgeteilt.',
            },
            {
              title: '6. Trennzeichen anpassen',
              detail:
                'Bei „Multiple labels" kann im Feld „Separator token" ein eigener Datensatz-Trenner eingetragen werden, falls „|" oder Leerzeilen in den Daten vorkommen.',
            },
            {
              title: '7. Felder prüfen',
              detail:
                'Die importierten Schlüssel werden automatisch an die Vorlagen-Variablen ({{feldName}}) angeglichen. Nicht zugeordnete Schlüssel werden angezeigt, damit Vorlage oder Datei angepasst werden können.',
            },
            {
              title: '8. Beispiel herunterladen',
              detail:
                'Über „Download Sample" wird eine .txt-Datei mit allen verfügbaren Vorlagen-Feldern als Muster generiert – so stimmen Schlüssel sofort überein.',
            },
            {
              title: '9. Drucken',
              detail:
                'Sobald die Daten geladen sind, erscheint in der Karte der Button „Print label(s)". Damit wird der Batch-Druck direkt gestartet – Vorschau und PDF enthalten die importierten Werte.',
            },
          ],
          tips: [
            'Schlüssel müssen nicht exakt den Vorlagen-Namen entsprechen: product_name, Product Name und productName werden gleich behandelt.',
            'Werte können Leerzeichen enthalten; Leerzeilen innerhalb eines Datensatzes werden als Trennzeichen für den nächsten Datensatz interpretiert, außer es wird ein Separator-Token gesetzt.',
            'Ein Backslash am Zeilenende fügt die nächste Zeile an den aktuellen Wert an – nützlich für lange Adressen oder Beschreibungen.',
            'Wenn ein Schlüssel nicht zugeordnet wird, prüfen, ob die Vorlage eine passende Variable enthält, und den Schlüssel in der .txt-Datei entsprechend umbenennen.',
            'Der Text-Import verwendet dieselbe Batch-Daten-Logik wie der CSV-Import; bereits geladene CSV-Daten werden beim Text-Import überschrieben und umgekehrt.',
          ],
        },
      },
    ],
  },
  {
    version: '1.6',
    date: 'April 8, 2025',
    highlights: 'KI-gestützter Label Creator, Sicherheits-Härtung',
    changes: [
      {
        type: 'feature',
        icon: <Sparkles className="w-4 h-4" />,
        title: 'AI Label Creator',
        description: 'Beschreibe dein Label in natürlicher Sprache und die KI generiert das Design automatisch – inklusive Text, Barcodes, QR-Codes und Formen. Erreichbar über den neuen „AI" Tab im Editor.',
        requiredPermissions: ['use_ai_creator' as PermissionType],
      },
      {
        type: 'improvement',
        icon: <Zap className="w-4 h-4" />,
        title: 'Sicherheits-Updates',
        description: 'Server-seitige Login-Sperre bei zu vielen Fehlversuchen, gehärtete Einladungslink-Validierung und verbesserter Schutz der Scan-Queue gegen unbefugten Zugriff.',
        adminOnly: true,
      },
      {
        type: 'fix',
        icon: <Bug className="w-4 h-4" />,
        title: 'AI-Elemente Crash behoben',
        description: 'Vom AI generierte Elemente werden jetzt korrekt in das interne Format konvertiert, sodass die Anwendung nicht mehr abstürzt.',
        requiredPermissions: ['use_ai_creator' as PermissionType],
      },
    ],
  },
  {
    version: '1.5',
    date: 'March 24, 2025',
    highlights: 'Feld-Verarbeitung: Letzte X Zeichen, Präfix-Dropdown aus Datasets, Strip-Zeichen – konfigurierbar vom PC',
    changes: [
      {
        type: 'feature',
        icon: <Sparkles className="w-4 h-4" />,
        title: 'Letzte X Zeichen',
        description: 'Pro Feld einstellbar: Nur die letzten X Zeichen eines gescannten Werts übernehmen. Konfiguration wird im Template gespeichert und automatisch auf dem Mobile Scanner angewendet.',
        requiredPermissions: ['use_barcode_scanner' as PermissionType],
      },
      {
        type: 'feature',
        icon: <Sparkles className="w-4 h-4" />,
        title: 'Präfix aus Dataset',
        description: 'Werte aus bestehenden Datasets als Präfix vor den Feldinhalt setzen – per Dropdown direkt im Feld auswählbar, auf PC und Mobile.',
        requiredPermissions: ['edit_field_data' as PermissionType],
      },
      {
        type: 'feature',
        icon: <Sparkles className="w-4 h-4" />,
        title: 'Strip-Zeichen pro Feld',
        description: 'Definierte Zeichen werden beim Scannen automatisch aus dem Wert entfernt. Einstellbar pro Feld im Template.',
        requiredPermissions: ['use_barcode_scanner' as PermissionType],
      },
      {
        type: 'improvement',
        icon: <Zap className="w-4 h-4" />,
        title: 'Desktop-Konfiguration für Mobile',
        description: 'Alle Feld-Verarbeitungsregeln (Letzte X Zeichen, Strip-Zeichen) können am PC konfiguriert und gespeichert werden – sie werden automatisch auf dem Mobile Scanner übernommen.',
      },
    ],
  },
  {
    version: '1.4.1',
    date: 'March 19, 2025',
    highlights: 'Stability fix for the Saved Templates panel',
    changes: [
      {
        type: 'fix',
        icon: <Bug className="w-4 h-4" />,
        title: 'Saved Tab Crash Fix',
        description: 'Fixed an issue where clicking the "Saved" tab in the editor could cause the entire page to go blank due to an invalid component value.',
      },
    ],
  },
  {
    version: '1.4',
    date: 'March 16, 2025',
    highlights: 'Major barcode upgrade: 15 formats, visual customization, validation, and improved rendering',
    changes: [
      {
        type: 'feature',
        icon: <Sparkles className="w-4 h-4" />,
        title: '15 Barcode Formats',
        description: 'Added Code 93, Codabar, EAN-13, EAN-8, UPC-A, ITF-14, Interleaved 2 of 5, GS1-128, Data Matrix, PDF417, MSI, Pharmacode, and Telepen — organized by category (General, Retail, Logistics, 2D, Specialty).',
      },
      {
        type: 'feature',
        icon: <Palette className="w-4 h-4" />,
        title: 'Barcode Color Customization',
        description: 'Set foreground and background colors on barcodes and QR codes for branded label designs.',
      },
      {
        type: 'feature',
        icon: <Settings className="w-4 h-4" />,
        title: 'Extended Barcode Controls',
        description: 'Bar width slider now ranges 0.5x–8x, bar height goes down to 10%, quiet zone up to 20 modules, and configurable text size below barcodes.',
      },
      {
        type: 'feature',
        icon: <Bug className="w-4 h-4" />,
        title: 'Barcode Validation',
        description: 'Real-time content validation warns when barcode data is invalid for the selected format (e.g. EAN-13 requires exactly 12–13 digits).',
      },
      {
        type: 'improvement',
        icon: <Zap className="w-4 h-4" />,
        title: 'Smart Format Switching',
        description: 'Changing barcode type auto-fills valid sample content when current data is incompatible, preventing render failures.',
      },
      {
        type: 'fix',
        icon: <Bug className="w-4 h-4" />,
        title: 'GS1-128 Encoding Fix',
        description: 'Fixed GS1-128 barcodes not rendering by properly encoding FNC1 application identifiers.',
      },
    ],
  },
  {
    version: '1.3',
    date: 'March 10, 2025',
    highlights: '20 new permissions, enhanced history search, and field data content search',
    changes: [
      {
        type: 'feature',
        icon: <Shield className="w-4 h-4" />,
        title: '20 New Permissions',
        description: 'Added granular permissions including keyboard shortcuts, barcode scanner, CSV import, reprint labels, bulk print, snap-to-grid, rulers, multi-select, resize elements, and more.',
        adminOnly: true,
      },
      {
        type: 'feature',
        icon: <Shield className="w-4 h-4" />,
        title: 'Collaboration & Audit Categories',
        description: 'New permission categories: Collaboration (view shared templates, comment on templates) and Audit (view own activity, view print history).',
        adminOnly: true,
      },
      {
        type: 'feature',
        icon: <History className="w-4 h-4" />,
        title: 'Search Label Field Content',
        description: 'History search now matches against actual label content (product names, serial numbers, barcode values) — not just template names or IDs.',
        requiredPermissions: ['view_label_history', 'view_print_history'],
      },
      {
        type: 'feature',
        icon: <History className="w-4 h-4" />,
        title: 'Print Source & Copies Filters',
        description: 'Filter label history by print source (Editor, Kiosk, Reprint) and copy count ranges (1, 2–5, 6–10, 10+).',
        requiredPermissions: ['view_label_history', 'view_print_history'],
      },
      {
        type: 'improvement',
        icon: <History className="w-4 h-4" />,
        title: 'Field Data Preview Chips',
        description: 'Each history card now shows up to 3 key field values as badges so you can identify labels at a glance without opening details.',
        requiredPermissions: ['view_label_history', 'view_print_history'],
      },
      {
        type: 'improvement',
        icon: <History className="w-4 h-4" />,
        title: 'Debounced Search & Clear Filters',
        description: 'History search is now debounced (300ms) for smoother typing, with a one-click "Clear filters" button when any filter is active.',
        requiredPermissions: ['view_label_history', 'view_print_history'],
      },
      {
        type: 'improvement',
        icon: <Keyboard className="w-4 h-4" />,
        title: 'Keyboard Shortcuts Permission',
        description: 'Keyboard shortcuts (undo, redo, copy, paste, print, zoom) are now gated behind the "use_keyboard_shortcuts" permission.',
        requiredPermissions: ['use_keyboard_shortcuts'],
      },
      {
        type: 'improvement',
        icon: <Printer className="w-4 h-4" />,
        title: 'Reprint Permission',
        description: 'Reprinting labels from history now requires the dedicated "reprint_labels" permission in addition to print access.',
        requiredPermissions: ['reprint_labels'],
      },
      {
        type: 'improvement',
        icon: <Database className="w-4 h-4" />,
        title: 'CSV Import Permission',
        description: 'CSV data import is now gated behind a dedicated "use_csv_import" permission for tighter data control.',
        requiredPermissions: ['use_csv_import'],
      },
      {
        type: 'improvement',
        icon: <Shield className="w-4 h-4" />,
        title: 'Barcode Scanner Permission',
        description: 'Barcode scanner input can now be controlled per user group via the "use_barcode_scanner" permission.',
        requiredPermissions: ['use_barcode_scanner'],
      },
    ],
  },
  {
    version: '1.2',
    date: 'March 10, 2025',
    highlights: 'Security enforcement, session management, and dynamic password policies',
    changes: [
      {
        type: 'feature',
        icon: <Shield className="w-4 h-4" />,
        title: 'Session Inactivity Timeout',
        description: 'Users are automatically logged out after a configurable period of inactivity, with a warning before logout.',
      },
      {
        type: 'feature',
        icon: <Shield className="w-4 h-4" />,
        title: 'Login Attempt Lockout',
        description: 'Accounts are temporarily locked after too many failed login attempts. Configurable in admin security settings.',
        adminOnly: true,
      },
      {
        type: 'improvement',
        icon: <Shield className="w-4 h-4" />,
        title: 'Dynamic Password Policy',
        description: 'Password requirements now respect admin-configured policies instead of using hardcoded rules.',
      },
      {
        type: 'improvement',
        icon: <Settings className="w-4 h-4" />,
        title: 'Admin Panel Rework',
        description: 'Redesigned admin panel with sidebar navigation, dashboard overview, and mobile hamburger menu.',
        adminOnly: true,
      },
      {
        type: 'improvement',
        icon: <Sparkles className="w-4 h-4" />,
        title: 'Role-Based Changelog',
        description: 'Release notes now filter entries based on your permissions — you only see updates relevant to features you can access.',
        requiredPermissions: ['view_changelog'],
      },
    ],
  },
  {
    version: '1.1',
    date: 'February 26, 2025',
    highlights: 'Automatic label storage, personal history, exact reprints, and role-based history access',
    changes: [
      {
        type: 'feature',
        icon: <History className="w-4 h-4" />,
        title: 'Automatic Label Storage',
        description: 'Every printed label is now saved automatically with a unique ID, complete snapshot of all field values, template, printer settings, and timestamp.',
        requiredPermissions: ['print_labels'],
      },
      {
        type: 'feature',
        icon: <History className="w-4 h-4" />,
        title: 'Personal Label History',
        description: 'Access your full print history from the new History page. Filter and search by date, template, or label ID.',
        requiredPermissions: ['view_label_history', 'view_print_history'],
      },
      {
        type: 'feature',
        icon: <Printer className="w-4 h-4" />,
        title: 'Exact Reprint Function',
        description: 'Reprint any previously saved label with the exact original configuration. Reprints generate a new UUID and are logged as separate events referencing the original.',
        requiredPermissions: ['reprint_labels'],
      },
      {
        type: 'feature',
        icon: <Shield className="w-4 h-4" />,
        title: 'History Viewer Role',
        description: 'New "view_label_history" permission allows read-only access to past label history without print or reprint capabilities.',
        adminOnly: true,
      },
      {
        type: 'feature',
        icon: <Users className="w-4 h-4" />,
        title: 'Admin Global History',
        description: 'Admins can view all users\' label histories, search globally, filter by user, and export history data to CSV.',
        adminOnly: true,
      },
      {
        type: 'improvement',
        icon: <Shield className="w-4 h-4" />,
        title: 'Audit Trail for Prints',
        description: 'All print and reprint actions are fully logged. Label records are immutable — no permanent deletion, soft delete only.',
        requiredPermissions: ['print_labels', 'view_own_activity'],
      },
    ],
  },
  {
    version: '1.0',
    date: 'February 16, 2025',
    highlights: 'Mandatory password change enforcement, admin controls, and security hardening',
    changes: [
      {
        type: 'feature',
        icon: <Shield className="w-4 h-4" />,
        title: 'Forced Password Change on Login',
        description: 'Users flagged for a password reset are now blocked from accessing the app until they set a new password.',
      },
      {
        type: 'feature',
        icon: <Users className="w-4 h-4" />,
        title: 'Per-User Password Change Toggle',
        description: 'Admins can toggle a "Force password change on next login" switch directly on each user card.',
        adminOnly: true,
      },
      {
        type: 'improvement',
        icon: <Shield className="w-4 h-4" />,
        title: 'Profile Loading Race Condition Fix',
        description: 'Protected routes now wait for the user profile to fully load before granting access.',
      },
      {
        type: 'improvement',
        icon: <Zap className="w-4 h-4" />,
        title: 'Weak Password Error Feedback',
        description: 'Password resets now surface clear error messages when a password is rejected for being too weak.',
        adminOnly: true,
      },
      {
        type: 'fix',
        icon: <Bug className="w-4 h-4" />,
        title: 'Password Change Flag Persistence',
        description: 'Fixed the issue where the password change flag would not clear after setting a new password.',
      },
    ],
  },
  {
    version: '0.9.1',
    date: 'February 12, 2025',
    highlights: 'Batch printing fix and preset deep-link improvements',
    changes: [
      {
        type: 'fix',
        icon: <Printer className="w-4 h-4" />,
        title: 'Batch Print Data Fix',
        description: 'Fixed a bug where batch printing/exporting used only the current row\'s data for all labels instead of each row\'s unique data from the CSV.',
        requiredPermissions: ['batch_import', 'bulk_print'],
      },
      {
        type: 'improvement',
        icon: <Globe className="w-4 h-4" />,
        title: 'Preset Deep-Links',
        description: 'URL deep-links now work with all presets and wait for authentication before loading.',
        requiredPermissions: ['use_presets'],
      },
    ],
  },
  {
    version: '0.9',
    date: 'February 3, 2025',
    highlights: 'Admin monitoring, security settings, and UI improvements',
    changes: [
      {
        type: 'feature',
        icon: <Activity className="w-4 h-4" />,
        title: 'Admin Monitoring Dashboard',
        description: 'New Monitoring tab with user activity logs, usage statistics, error tracking, and system health status.',
        adminOnly: true,
      },
      {
        type: 'feature',
        icon: <Settings className="w-4 h-4" />,
        title: 'Security Settings',
        description: 'Configure session timeouts, brute-force protection, and password policies from the admin panel.',
        adminOnly: true,
      },
      {
        type: 'feature',
        icon: <Database className="w-4 h-4" />,
        title: 'Audit Logging',
        description: 'All admin actions (user creation, password resets, deletions) are now logged for accountability.',
        adminOnly: true,
      },
      {
        type: 'feature',
        icon: <PanelLeftClose className="w-4 h-4" />,
        title: 'Resizable Layers Panel',
        description: 'Drag to resize the left panel. Content scrolls horizontally when narrowed to prevent cut-off.',
        requiredPermissions: ['use_layers_panel'],
      },
      {
        type: 'improvement',
        icon: <Zap className="w-4 h-4" />,
        title: 'Dynamic Template Presets',
        description: 'Template presets now load from the database, so changes appear instantly for all users.',
        requiredPermissions: ['use_presets'],
      },
      {
        type: 'improvement',
        icon: <Layout className="w-4 h-4" />,
        title: 'Wider Settings Dialog',
        description: 'Settings dialog is now wider to fit all content without scrolling.',
        requiredPermissions: ['change_settings'],
      },
      {
        type: 'improvement',
        icon: <Layout className="w-4 h-4" />,
        title: 'Larger Default Panel Size',
        description: 'The layers/templates panel now opens wider by default for better visibility.',
        requiredPermissions: ['use_layers_panel'],
      },
    ],
  },
  {
    version: '0.8',
    date: 'February 3, 2025',
    highlights: 'Global templates and improved admin controls',
    changes: [
      {
        type: 'feature',
        icon: <Globe className="w-4 h-4" />,
        title: 'Global Templates',
        description: 'Admins can now create and manage global templates that are visible to all users.',
        requiredPermissions: ['view_templates', 'view_shared_templates'],
      },
      {
        type: 'feature',
        icon: <Globe className="w-4 h-4" />,
        title: 'Template Import/Export',
        description: 'Import and export templates as JSON files. Admins can import directly to global templates.',
        requiredPermissions: ['import_templates'],
      },
      {
        type: 'improvement',
        icon: <Shield className="w-4 h-4" />,
        title: 'Template Visibility Control',
        description: 'Admins can toggle any template between private and global with a single click.',
        adminOnly: true,
      },
      {
        type: 'improvement',
        icon: <Layout className="w-4 h-4" />,
        title: 'Organized Template Panel',
        description: 'Templates are now organized into separate "Global Templates" and "My Templates" sections.',
        requiredPermissions: ['view_templates'],
      },
    ],
  },
  {
    version: '0.7',
    date: 'February 3, 2025',
    highlights: 'User authentication and cloud template storage',
    changes: [
      {
        type: 'feature',
        icon: <Users className="w-4 h-4" />,
        title: 'User Authentication',
        description: 'Username-based login system with admin approval workflow. No email required.',
      },
      {
        type: 'feature',
        icon: <Shield className="w-4 h-4" />,
        title: 'Role-Based Access Control',
        description: 'Admin panel for managing users, groups, and granular permissions.',
        adminOnly: true,
      },
      {
        type: 'feature',
        icon: <Database className="w-4 h-4" />,
        title: 'Cloud Template Storage',
        description: 'Templates are now saved per user in the cloud. Access your templates from any device.',
        requiredPermissions: ['view_templates'],
      },
      {
        type: 'feature',
        icon: <Shield className="w-4 h-4" />,
        title: 'Admin User Management',
        description: 'Admins can create users, reset passwords, approve accounts, and manage group memberships.',
        adminOnly: true,
      },
    ],
  },
  {
    version: '0.6',
    date: 'February 3, 2025',
    highlights: 'Kiosk mode overhaul and printing reliability',
    changes: [
      {
        type: 'fix',
        icon: <Printer className="w-4 h-4" />,
        title: 'Kiosk Mode Printing Fixed',
        description: 'Labels now correctly render text, barcodes, QR codes, and all elements when printing from Kiosk mode.',
        requiredPermissions: ['use_kiosk'],
      },
      {
        type: 'fix',
        icon: <Printer className="w-4 h-4" />,
        title: 'Improved Print Reliability',
        description: 'Print dialog now waits for all images, fonts, and barcodes to fully load before opening.',
        requiredPermissions: ['print_labels'],
      },
      {
        type: 'fix',
        icon: <Zap className="w-4 h-4" />,
        title: 'Barcode Rendering in Print',
        description: 'Barcodes now render correctly in printed output by properly awaiting canvas generation.',
        requiredPermissions: ['print_labels'],
      },
      {
        type: 'feature',
        icon: <Keyboard className="w-4 h-4" />,
        title: 'Enter Key Navigation in Kiosk',
        description: 'Press Enter to quickly advance to the next field when entering label data in Kiosk mode.',
        requiredPermissions: ['use_kiosk', 'use_barcode_scanner'],
      },
      {
        type: 'improvement',
        icon: <Layout className="w-4 h-4" />,
        title: 'Consistent Label Sizing',
        description: 'Label dimensions now correctly use sheet configuration settings across all print modes.',
        requiredPermissions: ['print_labels', 'configure_print_settings'],
      },
    ],
  },
  {
    version: '0.5',
    date: 'February 3, 2025',
    highlights: 'Keyboard shortcuts, layer reordering, and QR code variables',
    changes: [
      {
        type: 'feature',
        icon: <Keyboard className="w-4 h-4" />,
        title: 'Keyboard Shortcuts',
        description: 'Copy (Ctrl+C), paste (Ctrl+V), and delete (Del/Backspace) elements with keyboard shortcuts.',
        requiredPermissions: ['use_clipboard', 'use_keyboard_shortcuts'],
      },
      {
        type: 'feature',
        icon: <Layout className="w-4 h-4" />,
        title: 'Drag & Drop Layer Reordering',
        description: 'Reorder elements in the layers panel by dragging and dropping them to change their z-order.',
        requiredPermissions: ['use_layers_panel'],
      },
      {
        type: 'feature',
        icon: <Zap className="w-4 h-4" />,
        title: 'QR Code Variables',
        description: 'Use {{variable}} syntax in QR codes for dynamic content. Variables with the same name share a single input field.',
        requiredPermissions: ['manage_elements', 'edit_field_data'],
      },
      {
        type: 'improvement',
        icon: <Layout className="w-4 h-4" />,
        title: 'Persistent Panel States',
        description: 'Property panel sections now remember their collapsed/expanded state between sessions.',
        requiredPermissions: ['edit_properties'],
      },
      {
        type: 'fix',
        icon: <Bug className="w-4 h-4" />,
        title: 'Fixed Text in Custom Label Sizes',
        description: 'Text elements now display correctly when printing labels with non-standard dimensions.',
        requiredPermissions: ['print_labels'],
      },
      {
        type: 'fix',
        icon: <Bug className="w-4 h-4" />,
        title: 'Deduplicated Print Fields',
        description: 'Fixed duplicate input fields appearing in the print dialog when elements had matching field names and variables.',
        requiredPermissions: ['print_labels', 'edit_field_data'],
      },
    ],
  },
  {
    version: '0.4',
    date: 'February 3, 2025',
    highlights: 'Bug fixes and UI improvements',
    changes: [
      {
        type: 'fix',
        icon: <Bug className="w-4 h-4" />,
        title: 'Fixed Printing with Custom Label Sizes',
        description: 'PDF export and browser printing now correctly use your custom label dimensions instead of defaulting to standard sizes.',
        requiredPermissions: ['print_labels', 'configure_print_settings'],
      },
      {
        type: 'improvement',
        icon: <Layout className="w-4 h-4" />,
        title: 'Improved Properties Panel',
        description: 'Redesigned properties panel with collapsible sections, better organization, and more intuitive controls.',
        requiredPermissions: ['edit_properties'],
      },
      {
        type: 'improvement',
        icon: <Palette className="w-4 h-4" />,
        title: 'Better Dark Mode Contrast',
        description: 'Dark mode is now lighter with improved contrast for better readability.',
      },
    ],
  },
  {
    version: '0.3',
    date: 'February 2, 2025',
    highlights: 'Release notes and improved user experience',
    changes: [
      {
        type: 'feature',
        icon: <Sparkles className="w-4 h-4" />,
        title: 'Release Notes Dialog',
        description: 'Added a new release notes window to keep you updated on the latest features and improvements.',
        requiredPermissions: ['view_changelog'],
      },
      {
        type: 'improvement',
        icon: <Layout className="w-4 h-4" />,
        title: 'Improved Editor UI',
        description: 'Cleaner header design with better visual hierarchy and more intuitive controls.',
        requiredPermissions: ['access_editor'],
      },
      {
        type: 'feature',
        icon: <Keyboard className="w-4 h-4" />,
        title: 'Quick Print Shortcut',
        description: 'Press Ctrl+P (or Cmd+P on Mac) to instantly open the print dialog.',
        requiredPermissions: ['print_labels', 'use_keyboard_shortcuts'],
      },
    ],
  },
  {
    version: '0.2',
    date: 'February 1, 2025',
    highlights: 'Customization options and label sizing',
    changes: [
      {
        type: 'feature',
        icon: <Palette className="w-4 h-4" />,
        title: 'Custom Accent Colors',
        description: 'Personalize the editor with 8 different accent color options in the settings.',
        requiredPermissions: ['change_settings'],
      },
      {
        type: 'feature',
        icon: <Layout className="w-4 h-4" />,
        title: 'Dynamic Label Sizes',
        description: 'Choose from preset label sizes or enter custom dimensions in millimeters.',
        requiredPermissions: ['change_label_size'],
      },
      {
        type: 'improvement',
        icon: <Palette className="w-4 h-4" />,
        title: 'Theme Modes',
        description: 'Added Light, Gray, and Dark theme modes for comfortable editing in any environment.',
      },
    ],
  },
  {
    version: '0.1',
    date: 'January 28, 2025',
    highlights: 'Initial beta release',
    changes: [
      {
        type: 'feature',
        icon: <Printer className="w-4 h-4" />,
        title: 'Batch Printing',
        description: 'Print multiple labels at once with CSV data import support.',
        requiredPermissions: ['batch_import', 'use_csv_import'],
      },
      {
        type: 'feature',
        icon: <Zap className="w-4 h-4" />,
        title: 'Template Presets',
        description: 'Quick-start with pre-designed templates for common label types.',
        requiredPermissions: ['use_presets'],
      },
      {
        type: 'feature',
        icon: <Sparkles className="w-4 h-4" />,
        title: 'Label Designer',
        description: 'Full-featured label designer with drag-and-drop elements.',
        requiredPermissions: ['access_editor', 'resize_elements'],
      },
      {
        type: 'feature',
        icon: <Zap className="w-4 h-4" />,
        title: 'QR & Barcodes',
        description: 'Generate QR codes and various barcode formats directly on your labels.',
        requiredPermissions: ['manage_elements'],
      },
      {
        type: 'feature',
        icon: <Printer className="w-4 h-4" />,
        title: 'PDF Export',
        description: 'Export your labels to high-quality PDF files for printing.',
        requiredPermissions: ['export_pdf'],
      },
    ],
  },
];

const getTypeBadge = (type: 'feature' | 'improvement' | 'fix') => {
  switch (type) {
    case 'feature':
      return <Badge className="bg-primary/10 text-primary border-primary/20 hover:bg-primary/10">New</Badge>;
    case 'improvement':
      return <Badge className="bg-success/10 text-success border-success/20 hover:bg-success/10">Improved</Badge>;
    case 'fix':
      return <Badge className="bg-warning/10 text-warning border-warning/20 hover:bg-warning/10">Fixed</Badge>;
  }
};

export const ReleaseNotesDialog: React.FC<ReleaseNotesDialogProps> = ({
  open,
  onOpenChange,
}) => {
  const { isAdmin, hasPermission } = useAuth();

  const filteredReleases = useMemo(() => {
    return releaseNotes
      .map(release => {
        const visibleChanges = release.changes.filter(change => {
          // Admin-only entries
          if (change.adminOnly) return isAdmin;
          // Permission-gated entries: user needs at least one of the listed permissions
          if (change.requiredPermissions && change.requiredPermissions.length > 0) {
            return change.requiredPermissions.some(p => hasPermission(p));
          }
          // No restriction = visible to all
          return true;
        });
        return { ...release, changes: visibleChanges };
      })
      // Hide entire releases with 0 visible changes
      .filter(release => release.changes.length > 0);
  }, [isAdmin, hasPermission]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] sm:max-w-xl max-h-[80vh] flex flex-col p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-border/50 shrink-0">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10">
              <Sparkles className="w-5 h-5 text-primary" />
            </div>
            <div>
              <DialogTitle className="text-xl">What's New</DialogTitle>
              <p className="text-sm text-muted-foreground mt-0.5">
                Latest updates and improvements
              </p>
            </div>
          </div>
        </DialogHeader>
        
        <ScrollArea className="flex-1 min-h-0 overflow-auto">
          <div className="px-6 py-4 space-y-6">
            {filteredReleases.map((release, index) => (
              <div key={release.version}>
                <div className="flex items-center gap-3 mb-3">
                  <Badge variant="secondary" className="font-medium text-xs px-2.5 py-0.5 bg-primary/10 text-primary">
                    v{release.version}
                  </Badge>
                  <span className="text-xs text-muted-foreground">{release.date}</span>
                </div>
                
                {release.highlights && (
                  <p className="text-sm text-muted-foreground mb-4 pl-1">
                    {release.highlights}
                  </p>
                )}
                
                <div className="space-y-3">
                  {release.changes.map((change, changeIndex) => (
                    <div 
                      key={changeIndex}
                      className="flex gap-3 p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex-shrink-0 mt-0.5 text-muted-foreground">
                        {change.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-sm">{change.title}</span>
                          {getTypeBadge(change.type)}
                        </div>
                        <p className="text-xs text-muted-foreground leading-relaxed">
                          {change.description}
                        </p>
                        {change.guide && (
                          <Collapsible className="mt-2">
                            <CollapsibleTrigger className="flex items-center gap-1.5 text-xs font-medium text-primary hover:underline">
                              <BookOpen className="w-3.5 h-3.5" />
                              Detaillierte Anleitung
                              <ChevronDown className="w-3 h-3" />
                            </CollapsibleTrigger>
                            <CollapsibleContent className="mt-2 space-y-3 rounded-md border border-border/50 bg-background/60 p-3">
                              {change.guide.intro && (
                                <p className="text-xs text-muted-foreground leading-relaxed">
                                  {change.guide.intro}
                                </p>
                              )}

                              {change.guide.steps && (
                                <ol className="space-y-2">
                                  {change.guide.steps.map((step) => (
                                    <li key={step.title} className="text-xs">
                                      <span className="font-medium">{step.title}</span>
                                      <p className="text-muted-foreground leading-relaxed mt-0.5">
                                        {step.detail}
                                      </p>
                                    </li>
                                  ))}
                                </ol>
                              )}

                              {change.guide.table && (
                                <div className="space-y-1.5">
                                  <p className="text-xs font-medium">{change.guide.table.title}</p>
                                  <div className="rounded-md border border-border/50 divide-y divide-border/50">
                                    {change.guide.table.rows.map((row) => (
                                      <div key={row.label} className="flex gap-2 px-2 py-1.5">
                                        <span className="text-[11px] font-mono shrink-0 w-28">
                                          {row.label}
                                        </span>
                                        <span className="text-[11px] text-muted-foreground leading-relaxed">
                                          {row.value}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {change.guide.tips && (
                                <div className="space-y-1.5">
                                  <p className="text-xs font-medium">Hinweise & Tipps</p>
                                  <ul className="space-y-1 list-disc pl-4">
                                    {change.guide.tips.map((tip) => (
                                      <li
                                        key={tip}
                                        className="text-[11px] text-muted-foreground leading-relaxed"
                                      >
                                        {tip}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </CollapsibleContent>
                          </Collapsible>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                
                {index < filteredReleases.length - 1 && (
                  <Separator className="mt-6" />
                )}
              </div>
            ))}
          </div>
        </ScrollArea>
        
        <div className="px-6 py-4 border-t border-border/50 bg-muted/30">
          <p className="text-xs text-center text-muted-foreground">
            Thank you for using LabelBatchDesigner! 💙
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
